import React, { useMemo } from 'react'
import { IconAlertTriangle, IconSearch } from '@tabler/icons-react'
import { ReactComponent as InfoIcon } from '@assets/images/DataTable/info-circle.svg'
import { Button } from '../Button'
import { Checkbox } from '../Checkbox'
import DataTablePagination from './DataTablePagination.component'
import DataTableSearch from './DataTableSearch.component'
import type {
  DataTableColumn,
  DataTableExpandedTableConfig,
  DataTableProps,
  DataTableRow,
  DataTableSortDirection,
} from './DataTable.types'
import './DataTable.css'

const DEFAULT_ACTIONS = (
  <div className="ds-data-table__default-actions">
    {[0, 1, 2].map((index) => (
      <span
        key={index}
        className="ds-data-table__icon-action"
        aria-hidden="true"
      />
    ))}
  </div>
)

const DEFAULT_EXPANDED_CONTENT = (
  <>
    <p className="ds-data-table__expand-title">Title</p>
    <p className="ds-data-table__expand-description">Title</p>
  </>
)

const getCellValue = <T extends DataTableRow>(
  row: T,
  column: DataTableColumn<T>,
): React.ReactNode => {
  if (column.renderCell) {
    return column.renderCell(row)
  }

  const value = row[column.id]
  if (value == null || typeof value === 'boolean') {
    return 'Content'
  }

  return value
}

const getSortTriState = (
  columnId: string,
  sortColumnId?: string,
  sortDirection?: DataTableSortDirection,
): 'none' | 'asc' | 'desc' => {
  if (sortColumnId !== columnId || !sortDirection) {
    return 'none'
  }

  return sortDirection
}

const getAlignClass = (
  align: DataTableColumn['align'] | undefined,
  prefix:
    | 'ds-data-table__head-cell'
    | 'ds-data-table__cell'
    | 'ds-data-table__nested-head-cell'
    | 'ds-data-table__nested-cell',
): string => {
  if (align === 'center') {
    return ` ${prefix}--center`
  }

  if (align === 'right') {
    return ` ${prefix}--right`
  }

  return ''
}

type ExpandNode = {
  id: string
  children: ExpandNode[]
}

const collectDescendantIds = (node: ExpandNode): string[] =>
  node.children.flatMap((child) => [child.id, ...collectDescendantIds(child)])

const findExpandNode = (
  nodes: ExpandNode[],
  targetId: string,
): ExpandNode | null => {
  for (const node of nodes) {
    if (node.id === targetId) {
      return node
    }

    const nested = findExpandNode(node.children, targetId)
    if (nested) {
      return nested
    }
  }

  return null
}

const findSiblingIds = (
  nodes: ExpandNode[],
  targetId: string,
): string[] | null => {
  if (nodes.some((node) => node.id === targetId)) {
    return nodes.map((node) => node.id)
  }

  for (const node of nodes) {
    const nested = findSiblingIds(node.children, targetId)
    if (nested) {
      return nested
    }
  }

  return null
}

const getNestedExpandNodes = <
  NestedRow extends DataTableRow,
  ParentRow extends DataTableRow,
>(
  parentRow: ParentRow,
  config: DataTableExpandedTableConfig<NestedRow, ParentRow>,
): ExpandNode[] => {
  if (!config.expandable) {
    return []
  }

  return config.getRows(parentRow).map((nestedRow) => ({
    id: nestedRow.id,
    children: config.expandedTable
      ? getNestedExpandNodes(nestedRow, config.expandedTable)
      : [],
  }))
}

const nextExclusiveExpandedIds = (
  expandedRowIds: string[],
  rowId: string,
  tree: ExpandNode[],
): string[] => {
  const node = findExpandNode(tree, rowId)
  const descendantIds = node ? collectDescendantIds(node) : []

  if (expandedRowIds.includes(rowId)) {
    const toRemove = new Set([rowId, ...descendantIds])
    return expandedRowIds.filter((id) => !toRemove.has(id))
  }

  const siblingIds = findSiblingIds(tree, rowId) ?? [rowId]
  const toRemove = new Set(
    siblingIds.flatMap((siblingId) => {
      const siblingNode = findExpandNode(tree, siblingId)
      return siblingNode
        ? [siblingId, ...collectDescendantIds(siblingNode)]
        : [siblingId]
    }),
  )

  return [...expandedRowIds.filter((id) => !toRemove.has(id)), rowId]
}

const DataTable = <T extends DataTableRow>({
  columns,
  rows,
  groups,
  state = 'default',
  selectable = false,
  selectedRowIds = [],
  onSelectedRowIdsChange,
  expandable = false,
  expandableColumnId,
  expandedRowIds = [],
  onExpandedRowIdsChange,
  expandedTable,
  renderExpandedContent,
  showActions = true,
  actionsLabel = 'Action(s)',
  renderActions,
  search,
  pagination,
  emptyState,
  errorState,
  sortColumnId,
  sortDirection,
  onSort,
  onRowClick,
  className = '',
  darkMode = false,
  previewState,
}: DataTableProps<T>): React.ReactElement => {
  const isPreview = previewState != null
  const tableState = previewState ?? state
  const isGrouped = groups != null && groups.length > 0
  const isExpandable = expandable || isGrouped
  const resolvedExpandableColumnId = expandableColumnId ?? columns[0]?.id ?? ''

  const rowIds = useMemo(
    () =>
      isGrouped
        ? groups.flatMap((group) => group.rows.map((row) => row.id))
        : rows.map((row) => row.id),
    [groups, isGrouped, rows],
  )
  const expandTree = useMemo((): ExpandNode[] => {
    if (isGrouped) {
      return groups.map((group) => ({ id: group.id, children: [] }))
    }

    if (!expandable) {
      return []
    }

    return rows.map((row) => ({
      id: row.id,
      children: expandedTable ? getNestedExpandNodes(row, expandedTable) : [],
    }))
  }, [expandable, expandedTable, groups, isGrouped, rows])
  const allSelected =
    rowIds.length > 0 && rowIds.every((id) => selectedRowIds.includes(id))
  const someSelected =
    !allSelected && rowIds.some((id) => selectedRowIds.includes(id))

  /** Leading expand column only for grouped rows; flat expand uses in-cell control. */
  const leadingColumnCount = isGrouped ? 1 : selectable ? 1 : 0

  const columnCount =
    columns.length + leadingColumnCount + (showActions ? 1 : 0)

  const groupFillColSpan =
    columns.length > 1
      ? columns.length - 1 + (showActions ? 1 : 0)
      : showActions
        ? 1
        : 0

  const toggleAll = (): void => {
    if (!onSelectedRowIdsChange) {
      return
    }

    onSelectedRowIdsChange(allSelected ? [] : rowIds)
  }

  const toggleRow = (rowId: string): void => {
    if (!onSelectedRowIdsChange) {
      return
    }

    if (selectedRowIds.includes(rowId)) {
      onSelectedRowIdsChange(
        selectedRowIds.filter((selectedId) => selectedId !== rowId),
      )
      return
    }

    onSelectedRowIdsChange([...selectedRowIds, rowId])
  }

  const toggleExpanded = (rowId: string): void => {
    if (!onExpandedRowIdsChange) {
      return
    }

    onExpandedRowIdsChange(
      nextExclusiveExpandedIds(expandedRowIds, rowId, expandTree),
    )
  }

  const renderSortButton = (column: DataTableColumn<T>): React.ReactNode => {
    const sortTriState = getSortTriState(column.id, sortColumnId, sortDirection)

    return (
      <button
        type="button"
        className="ds-data-table__sort-btn"
        aria-label={`Sort by ${column.label}${
          sortTriState !== 'none' ? ` (${sortTriState})` : ''
        }`}
        disabled={isPreview}
        onClick={() => onSort?.(column.id)}
      >
        <span className="ds-data-table__sort-arrows" aria-hidden="true">
          <span
            className={`ds-data-table__sort-tri ds-data-table__sort-tri--up${
              sortTriState === 'asc' ? ' ds-data-table__sort-tri--active' : ''
            }`}
          />
          <span
            className={`ds-data-table__sort-tri ds-data-table__sort-tri--down${
              sortTriState === 'desc' ? ' ds-data-table__sort-tri--active' : ''
            }`}
          />
        </span>
      </button>
    )
  }

  const renderExpandButton = (
    rowId: string,
    isExpanded: boolean,
    labelPrefix = 'row',
  ): React.ReactNode => (
    <button
      type="button"
      className={`ds-data-table__expand-btn${
        isExpanded ? ' ds-data-table__expand-btn--expanded' : ''
      }`}
      aria-expanded={isExpanded}
      aria-label={
        isExpanded ? `Collapse ${labelPrefix}` : `Expand ${labelPrefix}`
      }
      disabled={isPreview}
      onClick={(event) => {
        event.stopPropagation()
        toggleExpanded(rowId)
      }}
    >
      <i className="mgc mgc_right_small_fill" aria-hidden="true" />
    </button>
  )

  const renderExpandableCellContent = <R extends DataTableRow>(
    row: R,
    column: DataTableColumn<R>,
    isExpanded: boolean,
    labelPrefix = 'row',
  ): React.ReactNode => (
    <div className="ds-data-table__expandable-cell">
      <span className="ds-data-table__expandable-cell-content">
        {getCellValue(row, column)}
      </span>
      {renderExpandButton(row.id, isExpanded, labelPrefix)}
    </div>
  )

  const renderNestedGutterCell = (
    variant: 'select' | 'actions',
  ): React.ReactNode => {
    const gutterClassName =
      variant === 'select'
        ? 'ds-data-table__cell ds-data-table__cell--select ds-data-table__nested-gutter'
        : 'ds-data-table__cell ds-data-table__cell--actions ds-data-table__nested-gutter'

    return <td className={gutterClassName} aria-hidden="true" />
  }

  const renderDataCells = (
    row: T,
    options?: {
      withExpandControl?: boolean
      isExpanded?: boolean
    },
  ): React.ReactNode => {
    const withExpandControl = options?.withExpandControl ?? false
    const isExpanded = options?.isExpanded ?? false

    return (
      <>
        {columns.map((column) => {
          const isExpandColumn =
            withExpandControl && column.id === resolvedExpandableColumnId

          return (
            <td
              key={`${row.id}-${column.id}`}
              className={`ds-data-table__cell${getAlignClass(column.align, 'ds-data-table__cell')}${
                isExpandColumn ? ' ds-data-table__cell--expandable' : ''
              }`}
            >
              {isExpandColumn
                ? renderExpandableCellContent(row, column, isExpanded)
                : getCellValue(row, column)}
            </td>
          )
        })}
        {showActions ? (
          <td className="ds-data-table__cell ds-data-table__cell--actions">
            {renderActions ? renderActions(row) : DEFAULT_ACTIONS}
          </td>
        ) : null}
      </>
    )
  }

  const renderGroupedRows = (): React.ReactNode =>
    groups?.map((group) => {
      const isExpanded = expandedRowIds.includes(group.id)

      return (
        <React.Fragment key={group.id}>
          <tr className="ds-data-table__row ds-data-table__row--group">
            <td
              className="ds-data-table__cell ds-data-table__cell--leading"
              aria-hidden="true"
            />
            <td className="ds-data-table__cell ds-data-table__cell--group-title">
              <div className="ds-data-table__expandable-cell">
                <span className="ds-data-table__expandable-cell-content">
                  {group.title}
                </span>
                {renderExpandButton(group.id, isExpanded, 'group')}
              </div>
            </td>
            {groupFillColSpan > 0 ? (
              <td
                colSpan={groupFillColSpan}
                className="ds-data-table__cell ds-data-table__cell--group-fill"
                aria-hidden="true"
              />
            ) : null}
          </tr>
          {isExpanded
            ? group.rows.map((row) => (
                <tr
                  key={row.id}
                  className="ds-data-table__row ds-data-table__row--group-child"
                >
                  <td
                    className="ds-data-table__cell ds-data-table__cell--leading ds-data-table__cell--indent"
                    aria-hidden="true"
                  />
                  {renderDataCells(row)}
                </tr>
              ))
            : null}
        </React.Fragment>
      )
    })

  const renderNestedExpandedRows = <
    NestedRow extends DataTableRow,
    ParentRow extends DataTableRow,
  >(
    parentRow: ParentRow,
    config: DataTableExpandedTableConfig<NestedRow, ParentRow>,
    depth = 0,
  ): React.ReactNode => {
    const nestedRows = config.getRows(parentRow)
    const nestedExpandable = Boolean(config.expandable)
    const nestedExpandColumnId =
      config.expandableColumnId ?? config.columns[0]?.id ?? ''
    const nestedShowActions = config.showActions ?? false
    const depthClass =
      depth > 0 ? ` ds-data-table__nested-depth-${Math.min(depth, 3)}` : ''

    return (
      <>
        <tr className={`ds-data-table__nested-head-row${depthClass}`}>
          {selectable ? renderNestedGutterCell('select') : null}
          {config.columns.map((column, columnIndex) => (
            <td
              key={column.id}
              colSpan={column.colSpan}
              className={`ds-data-table__nested-head-cell${getAlignClass(column.align, 'ds-data-table__nested-head-cell')}${
                columnIndex === 0 ? ' ds-data-table__nested-cell--indent' : ''
              }`}
            >
              {column.label}
            </td>
          ))}
          {showActions ? renderNestedGutterCell('actions') : null}
        </tr>
        {nestedRows.map((nestedRow) => {
          const isExpanded = expandedRowIds.includes(nestedRow.id)

          return (
            <React.Fragment key={nestedRow.id}>
              <tr
                className={`ds-data-table__row ds-data-table__nested-data-row${depthClass}${
                  nestedExpandable && isExpanded
                    ? ' ds-data-table__row--expanded'
                    : ''
                }`}
              >
                {selectable ? renderNestedGutterCell('select') : null}
                {config.columns.map((column, columnIndex) => {
                  const isExpandColumn =
                    nestedExpandable && column.id === nestedExpandColumnId
                  const isLeadingColumn = columnIndex === 0

                  return (
                    <td
                      key={`${nestedRow.id}-${column.id}`}
                      colSpan={column.colSpan}
                      className={`ds-data-table__nested-cell${getAlignClass(column.align, 'ds-data-table__nested-cell')}${
                        isExpandColumn ? ' ds-data-table__cell--expandable' : ''
                      }${isLeadingColumn ? ' ds-data-table__nested-cell--indent' : ''}`}
                    >
                      {isExpandColumn
                        ? renderExpandableCellContent(
                            nestedRow,
                            column,
                            isExpanded,
                            'nested row',
                          )
                        : getCellValue(nestedRow, column)}
                    </td>
                  )
                })}
                {showActions ? (
                  nestedShowActions ? (
                    <td className="ds-data-table__cell ds-data-table__cell--actions ds-data-table__nested-cell">
                      {DEFAULT_ACTIONS}
                    </td>
                  ) : (
                    renderNestedGutterCell('actions')
                  )
                ) : null}
              </tr>
              {nestedExpandable && isExpanded && config.expandedTable
                ? renderNestedExpandedRows(
                    nestedRow,
                    config.expandedTable,
                    depth + 1,
                  )
                : null}
              {nestedExpandable &&
              isExpanded &&
              !config.expandedTable &&
              config.renderExpandedContent ? (
                <tr className="ds-data-table__expand-row">
                  {selectable ? (
                    <td
                      className="ds-data-table__expand-cell ds-data-table__cell--select"
                      aria-hidden="true"
                    />
                  ) : null}
                  <td
                    colSpan={config.columns.length + (showActions ? 1 : 0)}
                    className="ds-data-table__expand-cell ds-data-table__expand-cell--content"
                  >
                    <div className="ds-data-table__expand-panel">
                      {config.renderExpandedContent(nestedRow)}
                    </div>
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          )
        })}
      </>
    )
  }

  const renderExpandedRowContent = (row: T): React.ReactNode => {
    if (renderExpandedContent) {
      return renderExpandedContent(row)
    }

    return DEFAULT_EXPANDED_CONTENT
  }

  const renderFlatRows = (): React.ReactNode =>
    rows.map((row) => {
      const isExpanded = expandedRowIds.includes(row.id)

      return (
        <React.Fragment key={row.id}>
          <tr
            className={`ds-data-table__row${
              isExpandable && isExpanded ? ' ds-data-table__row--expanded' : ''
            }${
              selectedRowIds.includes(row.id)
                ? ' ds-data-table__row--selected'
                : ''
            }${onRowClick ? ' ds-data-table__row--clickable' : ''}`}
            onClick={
              onRowClick
                ? (event) => {
                    const target = event.target as HTMLElement
                    if (target.closest('button, a, input, label')) return
                    onRowClick(row)
                  }
                : undefined
            }
          >
            {selectable ? (
              <td className="ds-data-table__cell ds-data-table__cell--select">
                <Checkbox
                  checked={selectedRowIds.includes(row.id)}
                  onChange={() => toggleRow(row.id)}
                  aria-label={`Select row ${row.id}`}
                  disabled={isPreview}
                  darkMode={darkMode}
                />
              </td>
            ) : null}
            {renderDataCells(row, {
              withExpandControl: isExpandable,
              isExpanded,
            })}
          </tr>
          {isExpandable && isExpanded && expandedTable
            ? renderNestedExpandedRows(row, expandedTable)
            : null}
          {isExpandable && isExpanded && !expandedTable ? (
            <tr className="ds-data-table__expand-row">
              {selectable ? (
                <td
                  className="ds-data-table__expand-cell ds-data-table__cell--select"
                  aria-hidden="true"
                />
              ) : null}
              <td
                colSpan={columns.length + (showActions ? 1 : 0)}
                className="ds-data-table__expand-cell ds-data-table__expand-cell--content"
              >
                <div className="ds-data-table__expand-panel">
                  {renderExpandedRowContent(row)}
                </div>
              </td>
            </tr>
          ) : null}
        </React.Fragment>
      )
    })

  const renderStatusBody = (): React.ReactNode => {
    if (tableState === 'loading') {
      return (
        <tbody>
          {Array.from({ length: 3 }).map((_, rowIndex) => (
            <tr
              key={`loading-${rowIndex}`}
              className="ds-data-table__row ds-data-table__row--skeleton"
            >
              {isGrouped ? (
                <td className="ds-data-table__cell ds-data-table__cell--leading">
                  <span className="ds-data-table__skeleton ds-data-table__skeleton--checkbox" />
                </td>
              ) : null}
              {!isGrouped && selectable ? (
                <td className="ds-data-table__cell ds-data-table__cell--select">
                  <span className="ds-data-table__skeleton ds-data-table__skeleton--checkbox" />
                </td>
              ) : null}
              {columns.map((column) => (
                <td
                  key={`${column.id}-${rowIndex}`}
                  className="ds-data-table__cell"
                >
                  <span className="ds-data-table__skeleton" />
                </td>
              ))}
              {showActions ? (
                <td className="ds-data-table__cell ds-data-table__cell--actions">
                  <div className="ds-data-table__default-actions">
                    {[0, 1, 2].map((index) => (
                      <span
                        key={index}
                        className="ds-data-table__skeleton ds-data-table__skeleton--action"
                      />
                    ))}
                  </div>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      )
    }

    if (tableState === 'empty') {
      return (
        <tbody>
          <tr className="ds-data-table__status-row">
            <td colSpan={columnCount} className="ds-data-table__status-cell">
              <div className="ds-data-table__status ds-data-table__status--empty">
                <span className="ds-data-table__status-icon ds-data-table__status-icon--empty">
                  <IconSearch size={16} stroke={2} />
                </span>
                <p className="ds-data-table__status-title">
                  {emptyState?.title ?? 'No result found'}
                </p>
                <p className="ds-data-table__status-description">
                  {emptyState?.description ?? "There's no data to show here"}
                </p>
                {emptyState?.actionLabel ? (
                  <Button
                    size="sm"
                    label={emptyState.actionLabel}
                    onClick={emptyState.onAction}
                    disabled={isPreview}
                  />
                ) : null}
              </div>
            </td>
          </tr>
        </tbody>
      )
    }

    if (tableState === 'error') {
      return (
        <tbody>
          <tr className="ds-data-table__status-row">
            <td colSpan={columnCount} className="ds-data-table__status-cell">
              <div className="ds-data-table__status ds-data-table__status--error">
                <span className="ds-data-table__status-icon ds-data-table__status-icon--error">
                  <IconAlertTriangle size={16} stroke={2} />
                </span>
                <p className="ds-data-table__status-title">
                  {errorState?.title ?? 'Failed to load'}
                </p>
                <p className="ds-data-table__status-description">
                  {errorState?.description ?? 'Network error. Try to refresh'}
                </p>
                {errorState?.actionLabel ? (
                  <Button
                    size="sm"
                    destructive
                    label={errorState.actionLabel}
                    onClick={errorState.onAction}
                    disabled={isPreview}
                  />
                ) : null}
              </div>
            </td>
          </tr>
        </tbody>
      )
    }

    return <tbody>{isGrouped ? renderGroupedRows() : renderFlatRows()}</tbody>
  }

  const renderLeadingHeader = (): React.ReactNode => {
    if (isGrouped) {
      return (
        <th
          scope="col"
          className="ds-data-table__head-cell ds-data-table__head-cell--leading"
        >
          {selectable ? (
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleAll}
              aria-label="Select all rows"
              disabled={isPreview || tableState !== 'default'}
              darkMode={darkMode}
            />
          ) : null}
        </th>
      )
    }

    if (!selectable) {
      return null
    }

    return (
      <th
        scope="col"
        className="ds-data-table__head-cell ds-data-table__head-cell--select"
      >
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={toggleAll}
          aria-label="Select all rows"
          disabled={isPreview || tableState !== 'default'}
          darkMode={darkMode}
        />
      </th>
    )
  }

  const renderColGroup = (): React.ReactNode => (
    <colgroup>
      {isGrouped || selectable ? (
        <col
          className={`ds-data-table__col${
            isGrouped
              ? ' ds-data-table__col--leading'
              : ' ds-data-table__col--select'
          }`}
        />
      ) : null}
      {columns.map((column) => (
        <col
          key={column.id}
          className="ds-data-table__col"
          style={column.width != null ? { width: column.width } : undefined}
        />
      ))}
      {showActions ? (
        <col className="ds-data-table__col ds-data-table__col--actions" />
      ) : null}
    </colgroup>
  )

  const renderTableHeader = (): React.ReactNode => (
    <thead>
      <tr>
        {renderLeadingHeader()}
        {columns.map((column) => (
          <th
            key={column.id}
            scope="col"
            className={`ds-data-table__head-cell${getAlignClass(column.align, 'ds-data-table__head-cell')}`}
          >
            <div
              className={`ds-data-table__head-inner${
                column.showInfo ? ' ds-data-table__head-inner--with-info' : ''
              }`}
            >
              <span className="ds-data-table__head-label">
                {column.label}
                {column.showInfo ? (
                  <button
                    type="button"
                    className="ds-data-table__info-btn"
                    aria-label={`More information about ${column.label}`}
                    disabled={isPreview}
                  >
                    <InfoIcon aria-hidden="true" />
                  </button>
                ) : null}
              </span>
              {column.sortable ? renderSortButton(column) : null}
            </div>
          </th>
        ))}
        {showActions ? (
          <th
            scope="col"
            className="ds-data-table__head-cell ds-data-table__head-cell--actions"
          >
            {actionsLabel}
          </th>
        ) : null}
      </tr>
    </thead>
  )

  return (
    <div
      className={`ds-data-table${isPreview ? ' ds-data-table--preview' : ''}${
        darkMode ? ' ds-data-table--dark' : ''
      } ${className}`.trim()}
    >
      {search ? (
        <div className="ds-data-table__toolbar">
          <DataTableSearch {...search} disabled={isPreview} />
        </div>
      ) : null}
      <div className="ds-data-table__frame">
        <table className="ds-data-table__table">
          {renderColGroup()}
          {renderTableHeader()}
          {renderStatusBody()}
        </table>
      </div>

      {pagination ? (
        <DataTablePagination {...pagination} darkMode={darkMode} />
      ) : null}
    </div>
  )
}

export default DataTable
